import * as React from "react";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { FilePenLine, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InventoryLedgerItem, ItemType } from "@/gen/inventory/common_pb";
import { pharmacyClient } from "@/connect";
import { toast } from "@/components/ui/use-toast";
import dayjs from "dayjs";

interface ItemLedgerSheetProps {
    itemId: string;
    itemType: ItemType;
}

export const ItemLedgerSheet: React.FC<ItemLedgerSheetProps> = ({
    itemId,
    itemType
}) => {

    const [loading, setLoading] = React.useState(true);
    const [ledgerData, setLedgerData] = React.useState<InventoryLedgerItem[]>([]);

    React.useEffect(() => {
        if (itemId && itemType) {
            pharmacyClient.inventoryLedger({
                batchId: itemId,
                itemType: itemType
            }).then((response) => {
                setLedgerData(response.inventoryLedgerItems || []);
                setLoading(false);
            }).catch((error) => {
                console.error("Error fetching item ledger:", error);
                setLoading(false);
                toast({
                    title: "Error",
                    description: "Failed to fetch item ledger. Please try again later.",
                    variant: "destructive",
                    duration: 5000,
                });
            });
        }
    }, [itemId, itemType])

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button variant="ghost">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth="2" />
                        <line x1="8" y1="8" x2="16" y2="8" strokeWidth="2" />
                        <line x1="8" y1="12" x2="16" y2="12" strokeWidth="2" />
                        <line x1="8" y1="16" x2="12" y2="16" strokeWidth="2" />
                    </svg>
                </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>Item Ledger</SheetTitle>
                    <SheetClose asChild>
                        <Button variant="ghost" className="absolute top-4 right-4">
                            <X className="h-4 w-4" />
                        </Button>
                    </SheetClose>
                </SheetHeader>
                <div className="mt-4">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Qty Change</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Stock Point</TableHead>
                                <TableHead>User</TableHead>
                                <TableHead>Notes</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-16" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                ledgerData.map((entry, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{dayjs(entry.transactionDate?.toDate()).format("DD/MM/YYYY HH:mm:ss")}</TableCell>
                                        <TableCell>
                                            <Badge variant="secondary">{entry.transactionType}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={
                                                    entry.quantityChange < 0
                                                        ? "font-bold text-red-900"
                                                        : "font-bold text-green-900"
                                                }
                                            >
                                                {entry.quantityChange.toFixed(2)}
                                            </span>
                                        </TableCell>
                                        <TableCell>{entry.quantityCategory}</TableCell>
                                        <TableCell>{entry.stockPointName}</TableCell>
                                        <TableCell>{entry.username}</TableCell>
                                        <TableCell>{entry.notes || "-"}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </SheetContent>
        </Sheet>
    );
};